/**
 * ChoiceContainer component.
 */
import ApiConfig from '@api';
import ChannelsClassicIcon from '@components/atoms/icons/channelsClassicIcon';
import ChannelsGridIcon from '@components/atoms/icons/channelsGridIcon';
import ChannelsLineupIcon from '@components/atoms/icons/channelsLineupIcon';
import ChannelView from '@shared/types/enums/channelView';
import ChannelViewContainer from '@components/elements/choice/channelViewContainer';
import ChannelsChoiceListContainer from '@components/atoms/container/channelsChoiceListContainer';
import FlavourConfig from '@shared/config/flavours';
import Focus from '@shared/types/enums/focus';
import List from '@components/sections/list/list';
import TranslationManager from '@shared/assets/locale/translationsManager';
import { useCallback } from 'react';
import { setFocus } from '@shared/store/slices/focus.slice';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { userSelector } from '@shared/store/slices/user.slice';

/**
 * Props for the ChoiceContainer component.
 */
type Props = {
  onSelectView?: (index: number) => void;
};

/**
 * Renders a container with the channels choice views.
 */
export default function ChannelsChoiceList({ onSelectView }: Props) {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Dispatch
  const dispatch = useAppDispatch();

  // Selectors
  const { carrierId } = useAppSelector(userSelector);

  // ------------------------------------------------------------
  // Views
  // ------------------------------------------------------------

  const views = [
    {
      icon: ChannelsGridIcon,
      viewType: ChannelView.Grid,
      textKey: TranslationManager.get('PAGES.CHANNELS_CHOICE.VIEWS.GRID_VIEW'),
      image: '',
    },
    {
      icon: ChannelsClassicIcon,
      textKey: TranslationManager.get(
        'PAGES.CHANNELS_CHOICE.VIEWS.CLASSIC_VIEW'
      ),
      viewType: ChannelView.Classic,
      image: ApiConfig.urls.images.channelsClassic.replace(
        '{OPERATOR_ID}',
        carrierId || FlavourConfig.carrierId
      ),
    },
    {
      icon: ChannelsLineupIcon,
      textKey: TranslationManager.get(
        'PAGES.CHANNELS_CHOICE.VIEWS.LINEUP_VIEW'
      ),
      viewType: ChannelView.Lineup,
      image: ApiConfig.urls.images.channelsDial.replace(
        '{OPERATOR_ID}',
        carrierId || FlavourConfig.carrierId
      ),
    },
  ];

  // ------------------------------------------------------------
  // Event handlers
  // ------------------------------------------------------------

  const onEnter = useCallback(
    (view: ChannelView) => {
      onSelectView?.(view);
    },
    [onSelectView]
  );

  const outOfBounds = (focus: string | number, key?: string) => {
    if (key === 'LEFT') {
      dispatch(setFocus(Focus.MENU));
    }
  };

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------

  return (
    <ChannelsChoiceListContainer>
      <List focus={Focus.CHANNELS_CHOICE} outOfBounds={outOfBounds}>
        {views.map((item, index) => (
          <ChannelViewContainer
            key={index}
            index={index}
            text={item.textKey}
            Icon={item.icon}
            image={item.image}
            action={() => {
              onEnter(item.viewType);
            }}
          />
        ))}
      </List>
    </ChannelsChoiceListContainer>
  );
}
